import React, {
  Fragment,
  JSX,
  KeyboardEvent,
  ReactElement,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  defaultPlaylistName,
  Hymn,
  Hymnal,
  isMidiFileMissing,
  OrgType,
  SongList,
  SongSlug,
} from '../../../common/src/model.ts';
import { isBound } from '../../../common/src/model_util.ts';
import { Pages } from '../../../common/src/pages.ts';
import { buildSearchRegex } from '../../../common/src/search.ts';
import {
  decideSongClickGate,
  hasHouseholdChoiceSundaySongAccess,
  hasSubscriptionLikeSongAccess,
} from '../../../common/src/subscription_banner_policy.ts';
import { ensureExists } from '../../../common/src/util.ts';
import TrashIcon from '../assets/trash.svg?react';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { getSongListFromHymnal, getSongListFromHymns, HymnalWithHymns, isSongSelectorTitle } from '../data/hymnals.ts';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { useHousehold } from '../data/use_household.tsx';
import { FavoritesHymnalId, HymnalsContext } from '../data/use_hymnals.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { Footer } from '../pages/footer.tsx';
import { Header } from '../pages/header.tsx';
import { activateSearch as onboardingActivateSearch } from '../pages/onboarding/common.tsx';
import { SubscriptionBanner } from '../pages/subscription_banner.tsx';
import { SequencerContext } from '../player/sequencer.tsx';
import { isInsideMobileApp } from '../util/billing.ts';
import { isOptimisticallySubscribed } from '../util/optimistic_subscription.ts';
import {
  bubbleExactNumericMatchesToTop,
  generateHymnUrl,
  getPageFromLocation,
  getUrlForPage,
  isCustomSong,
  PrimaryOrgHymnalId,
} from '../util/path.ts';
import { getPlatform, Platform } from '../util/platform.ts';
import { SearchContext, SearchControl } from '../util/search_control.tsx';
import { BackArrow, InnerPageContent, OuterPageContent, Pencil, RedDotIcon } from '../util/shared.tsx';
import { Spinner } from '../util/spinner.tsx';
import { storeProspectiveSubscriptionOrigin } from '../util/subscription_flow_origin.ts';
import { useCurrentDate } from '../util/use_current_date.tsx';
import { useMusicCache } from '../util/use_music_cache.tsx';
import { useShowTutorialsButton } from '../util/use_show_tutorials_button.ts';
import useWindowDimensions from '../util/useWindowDimensions.ts';
import { HymnalsView } from './hymnals_view.tsx';
import { SongDisplayName, WrapHymnNames } from './song_display_name.tsx';
import { isSongClickEnabled, isSongRowDimmed } from './song_row_policy.ts';

export const LibraryPageTitle = 'Library';
const MaxSearchResultsPerHymnal = 50;
export const HymnRowHeight = WrapHymnNames ? '2.3lh' : '1.7lh';

const hymnalAccordionThresholds: Array<{ screenHeight: number; maxHeadingCount: number }> = [
  { screenHeight: 500, maxHeadingCount: 3 },
  { screenHeight: 700, maxHeadingCount: 4 },
  { screenHeight: 1000, maxHeadingCount: 5 },
];

export interface Props {
  customHeader?: JSX.Element;
  hymnals: HymnalWithHymns[];
  title: string | React.ReactElement;
  weeklySongGroups?: HymnalWithHymns[];
  message?: JSX.Element;
  isDemo?: boolean;
  isFooterVisibleForDemo?: boolean; // HymnsList should not be displaying the footer... :/
  showThisSundayOnly?: boolean;
  suppressHeader?: boolean;
  onEditHymnal?: (hymnal: HymnalWithHymns) => void;
  activateSearch?: boolean;
  onAddSong?: (songName?: string) => void;
  onSongClick?: (songSlug: SongSlug | undefined) => void;
  onSearchKeyDownProp?: (event: KeyboardEvent<HTMLInputElement>, searchString?: string) => void;
  onPreviewHymn?: (hymn: Hymn | undefined) => void;
  onClose?: () => void;
  onHeaderEditClick?: () => void;
  isInSongEditMode?: boolean;
  playButton?: JSX.Element;
  isInSongSelector?: boolean;
  selectSearchValue?: boolean;
  deleteButtonVisible?: boolean;
  isCurrentHymnalSticky?: boolean;
  areTopHymnalsFancy?: boolean;
  headerAfterFirstHymnal?: JSX.Element;
  firstElement?: JSX.Element;
  isUploadSongButtonVisible?: boolean;
  showProfileAvatar?: boolean; // main tabs only (Library, Favorites, Organization)
}

export const HymnsList = ({
  customHeader,
  hymnals,
  title,
  weeklySongGroups,
  message,
  isDemo,
  isFooterVisibleForDemo,
  showThisSundayOnly,
  onEditHymnal,
  activateSearch,
  onAddSong,
  onSongClick,
  onSearchKeyDownProp,
  onPreviewHymn,
  onClose,
  onHeaderEditClick,
  isInSongEditMode,
  playButton,
  isInSongSelector,
  selectSearchValue,
  deleteButtonVisible,
  isCurrentHymnalSticky,
  areTopHymnalsFancy,
  headerAfterFirstHymnal,
  firstElement,
  isUploadSongButtonVisible,
  showProfileAvatar,
}: Props) => {
  useMusicCache();
  const { onTutorialsClick } = useShowTutorialsButton();
  const [loading, setLoading] = useState<boolean>(!!isInSongSelector);
  const { searchString, setSearchString, searchHasFocus } = use(SearchContext);
  const allHymnals = use(HymnalsContext);
  const [searchIsVisible, setSearchIsVisible] = useState(() => Boolean(!isDemo && (searchString || activateSearch)));
  const [currentHymnalName, setCurrentHymnalName] = useLocalStorage(LocalStorageKey.CurrentHymnalName);
  const [currentHymnal, setCurrentHymnal] = useState<Hymnal | undefined>(
    isCurrentHymnalSticky
      ? hymnals.find((hymnal) => hymnal.name === currentHymnalName)
      : hymnals.length === 1 && hymnals[0].id === FavoritesHymnalId
        ? hymnals[0]
        : undefined,
  );
  const { isInternalUser, isOrgAdmin, isStaff } = useUserAttributes();
  const { currentOrg } = useCurrentOrg();
  const { household } = useHousehold();
  const navigate = useNavigate();
  const clientDate = useCurrentDate();

  // state var for whether to display player or not, sort of like a boolean but is a Hymn obj
  // initialized to undefined
  const { openPlayer } = use(SequencerContext);
  const location = useLocation();
  const page = getPageFromLocation(location);
  const windowDimensions = useWindowDimensions();
  const [ignoreMouseHover, setIgnoreMouseHover] = useState(false);
  const isBypassUser = isInternalUser;
  const hasOptimisticSubscription = isOptimisticallySubscribed({ household });
  const hasSubscriptionLikeAccess = hasSubscriptionLikeSongAccess({
    page,
    organization: currentOrg,
    household,
    clientDate,
    isBypassUser,
    isOptimisticallySubscribed: hasOptimisticSubscription,
  });
  const hasSundaySongAccess = hasHouseholdChoiceSundaySongAccess({
    page,
    organization: currentOrg,
    clientDate,
  });

  const shouldActivateSearch = onboardingActivateSearch() || activateSearch;

  // if one of the primary hymnals is open, we treat it as a single hymnal
  const isPrimaryHymnalOpen =
    currentHymnal !== undefined && weeklySongGroups?.some(({ id }) => id === PrimaryOrgHymnalId);
  const singleHymnal =
    // A single weekly song group would not be "singleHymnal" (we would still want to display headers)
    // However, we need to be able to open "Our Hymnal" as a single hymnal
    (!weeklySongGroups || currentHymnal?.id === PrimaryOrgHymnalId || isPrimaryHymnalOpen) &&
    // Note: hymnals.length check allows Favorites to bypass MaxSearchResultsPerHymnal
    (currentHymnal !== undefined || hymnals.length === 1);

  const showingWeeklySongLists = weeklySongGroups && !singleHymnal;
  const hymnsListTitle = currentHymnal?.name ?? title;
  const disablePaidContent = !hasSubscriptionLikeAccess && !hasSundaySongAccess;
  const isSingleStaticList = weeklySongGroups?.length === 1 && weeklySongGroups[0]!.name === '';
  const maxSearchResultsPerHymnal =
    !singleHymnal && !isSingleStaticList && !weeklySongGroups?.some(({ id }) => id === PrimaryOrgHymnalId)
      ? MaxSearchResultsPerHymnal
      : Number.MAX_SAFE_INTEGER;

  const songClickIsEnabled = useCallback(
    (hymnal?: Hymnal) =>
      isSongClickEnabled({
        page,
        hymnal,
        hymnals: allHymnals,
        org: currentOrg,
        hasPaidContentAccess: !disablePaidContent,
        isShowingWeeklySongLists: !!showingWeeklySongLists,
        searchString,
      }),
    [allHymnals, currentOrg, disablePaidContent, page, showingWeeklySongLists, searchString],
  );
  const songClickIsGated = useCallback(
    (hymnal?: Pick<Hymnal, 'id'>) =>
      decideSongClickGate({
        page,
        organization: currentOrg,
        household,
        clientDate,
        isOrganizationHymnal: hymnal?.id === PrimaryOrgHymnalId,
        isBypassUser,
        isOptimisticallySubscribed: hasOptimisticSubscription,
      }).variant === 'gate',
    [clientDate, currentOrg, hasOptimisticSubscription, household, isBypassUser, page],
  );
  const [selectedSongIndex, setSelectedSongIndex] = useState<number>(0);

  const isFancyHymnalListVisible = useMemo(() => {
    const maybeFavorites = hymnals.length === 1; // TODO(hewitt): remove when we separate Hymnals from HymnsList
    return !weeklySongGroups && !searchString && !maybeFavorites && !currentHymnal;
  }, [currentHymnal, hymnals.length, searchString, weeklySongGroups]);

  useEffect(() => {
    if (isDemo) {
      setSearchString('');
    }
  }, [isDemo, setSearchString]);

  useEffect(() => {
    const loadingTimeout = setTimeout(() => setLoading(false));
    return () => clearTimeout(loadingTimeout);
  }, []);

  useEffect(() => {
    if (!isCurrentHymnalSticky) {
      return;
    }
    setCurrentHymnalName(currentHymnal?.name);
  }, [currentHymnal, hymnals, isCurrentHymnalSticky, setCurrentHymnalName]);

  const searchRegEx = useMemo(() => buildSearchRegex(searchString), [searchString]);

  const getVisibleHymns = useCallback(
    (hymnal: HymnalWithHymns) => {
      if (!searchString) {
        return hymnal.hymns;
      }
      const matchingHymns = hymnal.hymns.filter((hymn) => {
        const { songNumber } = hymn;
        const psalmSuffix =
          hymn.psalm && !/psalm|psaume/.test(ensureExists(hymn.title).toLowerCase()) ? ' - ' + hymn.psalm : '';
        const hymnString = songNumber + ' - ' + hymn.title + psalmSuffix;
        return searchRegEx ? searchRegEx.test(hymnString) : true;
      });
      if (hymnal.id === PrimaryOrgHymnalId) {
        return bubbleExactNumericMatchesToTop(matchingHymns, searchString);
      }
      return matchingHymns;
    },
    [searchRegEx, searchString],
  );

  // Hacky performance optimization.  Instead of these variables being useState instances updated by a useEffect,
  // we recalculate them on every render because the performance is better (go figure).  Ultimately, we need
  // to break down the monolithic HymnsList component into reusable subcomponents, which will enable us to
  // better reason regarding the interdependencies.  Note that when these variables were useState instances, opening
  // a hymnal became very slow, and the big hint was that the church hymnal rendered for a moment before rendering
  // the newly selected hymnal.  This fact could guide us to a better solution in the future.
  const { visibleHymnals, displayHymnalTitle, listOfHymns } = useMemo(() => {
    const tempList: Hymn[] = [];

    let preFilteredHymnals: HymnalWithHymns[];
    let displayHymnalTitle: boolean;

    if (!searchString && showingWeeklySongLists && weeklySongGroups) {
      displayHymnalTitle = !(weeklySongGroups.length === 1 && weeklySongGroups[0].hideTitle);
      preFilteredHymnals = weeklySongGroups.map((hymnal) => {
        if (hymnal.name !== '') {
          return hymnal;
        }
        const hymns = hymnals.find(({ id }) => id === PrimaryOrgHymnalId)?.hymns ?? [];
        return { ...hymnal, hymns };
      });
    } else if (currentHymnal !== undefined) {
      preFilteredHymnals = hymnals.filter((hymnal) => hymnal.id === currentHymnal?.id);
      displayHymnalTitle = false;
    } else if (weeklySongGroups?.some(({ id }) => id === PrimaryOrgHymnalId)) {
      preFilteredHymnals = weeklySongGroups;
      const multiplePrimaryHymnals = weeklySongGroups?.length > 2;
      displayHymnalTitle = multiplePrimaryHymnals;
    } else {
      preFilteredHymnals = hymnals;
      displayHymnalTitle = !(hymnals.length === 1 && hymnals[0].hideTitle);
    }

    const hymnalsToRender: HymnalWithHymns[] = preFilteredHymnals
      .filter(
        (hymnal) =>
          isInternalUser ||
          // an internal hymnal remains visible to a church that lists it among its primary
          // hymnals - keeps a non-cleared hymnal generally hidden yet available to select
          // churches (issue #2933)
          ((!hymnal.isInternal || currentOrg?.primaryHymnalIds?.includes(hymnal.id)) &&
            !hymnal.isChurchSpecific &&
            (!isInSongSelector ||
              !hymnal.isHiddenInSongSelector ||
              currentOrg?.hymnals?.some(({ id }) => id === hymnal.id)) &&
            // the Library lists every title of a book, the song selector one (issue #3454)
            (!isInSongSelector ||
              isSongSelectorTitle(allHymnals, hymnal, {
                hymnals: currentOrg?.hymnals,
                primaryHymnalIds: currentOrg?.primaryHymnalIds,
              }))),
      )
      .map((hymnal) => ({ ...hymnal, hymns: getVisibleHymns(hymnal) }))
      .map((hymnal) => ({ ...hymnal, message: hymnal.hymns.length === 0 ? hymnal.message : undefined }))
      .filter(
        (hymnal) => hymnal.hymns.length > 0 || hymnal.message || (hymnal.id === PrimaryOrgHymnalId && !searchString),
      );

    for (const hymnal of hymnalsToRender) {
      for (const hymn of getVisibleHymns(hymnal)) {
        tempList.push(hymn);
      }
    }
    return { visibleHymnals: hymnalsToRender, displayHymnalTitle, listOfHymns: tempList };
  }, [
    allHymnals,
    currentHymnal,
    currentOrg?.hymnals,
    currentOrg?.primaryHymnalIds,
    getVisibleHymns,
    hymnals,
    isInSongSelector,
    isInternalUser,
    searchString,
    showingWeeklySongLists,
    weeklySongGroups,
  ]);

  useEffect(() => {
    // this is reasonable - we want to reset the selected song whenever the search result count changes
    // eslint-disable-next-line
    setSelectedSongIndex(0);
  }, [listOfHymns.length]);

  useEffect(() => {
    onPreviewHymn?.(isFancyHymnalListVisible ? undefined : listOfHymns[selectedSongIndex]);
  }, [isFancyHymnalListVisible, isInSongSelector, onPreviewHymn, searchString, selectedSongIndex, listOfHymns]);

  const suppressSongIssue = useCallback(
    (hymnal: HymnalWithHymns, hymn: Hymn) => {
      return (
        hymnal.id === PrimaryOrgHymnalId ||
        hymnal.id === FavoritesHymnalId ||
        page === Pages.Organization || // TODO(hewitt): should be isPrimaryHymnal(hymnal) instead...
        (!searchString && !singleHymnal && Boolean(weeklySongGroups || (isInSongSelector && !isMidiFileMissing(hymn))))
      );
    },
    [page, searchString, singleHymnal, weeklySongGroups, isInSongSelector],
  );

  const handleArrowKey = useCallback(
    (arrow: string) => {
      const newIndex = Math.max(
        0,
        Math.min(listOfHymns.length - 1, selectedSongIndex + (arrow === 'ArrowDown' ? 1 : -1)),
      );
      setSelectedSongIndex(newIndex);
      setIgnoreMouseHover(true); // prevent mouse from contending with keyboard for selection
      setTimeout(() => setIgnoreMouseHover(false)); // allow the mouse to take over after the keyboard closes
    },
    [selectedSongIndex, listOfHymns.length],
  );

  const getHymnalForVisibleSongIndex = useCallback(
    (targetIndex: number): HymnalWithHymns | undefined => {
      let currentIndex = 0;
      for (const hymnal of visibleHymnals) {
        const nextIndex = currentIndex + hymnal.hymns.length;
        if (targetIndex >= currentIndex && targetIndex < nextIndex) {
          return hymnal;
        }
        currentIndex = nextIndex;
      }
    },
    [visibleHymnals],
  );

  const onKeyUp = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'Enter': {
          const hymn = listOfHymns[selectedSongIndex];
          if (!hymn) {
            return;
          }
          const songList = getSongListFromHymns('Search', listOfHymns);
          if (songClickIsGated(getHymnalForVisibleSongIndex(selectedSongIndex))) {
            void openSubscriptionSongGate({
              navigate,
              originPage: page,
              playlist: songList,
              playlistIndex: selectedSongIndex,
              songTitle: hymn.title,
            });
            return;
          }
          if (onSongClick) {
            onSongClick(hymn.slug);
          } else {
            // TODO(hewitt): under what circumstances is this code path executed?
            openPlayer({ playlist: getSongListFromHymnal({ name: defaultPlaylistName, hymns: [hymn] }) });
          }
          break;
        }
        case 'ArrowDown':
          handleArrowKey(event.key);
          break;
        case 'ArrowUp':
          handleArrowKey(event.key);
          break;
      }
    },
    [
      getHymnalForVisibleSongIndex,
      handleArrowKey,
      listOfHymns,
      page,
      selectedSongIndex,
      songClickIsGated,
      onSongClick,
      openPlayer,
      navigate,
    ],
  );

  function hymnalNameShouldBeWrapped(hymnalName: string) {
    // only wrap if the time or location is specified
    return hymnalName.includes('@');
  }

  const maxHeadingCount: number = useMemo(() => {
    for (const { screenHeight, maxHeadingCount } of hymnalAccordionThresholds) {
      if (windowDimensions.height <= screenHeight) {
        return maxHeadingCount;
      }
    }
    return hymnalAccordionThresholds[hymnalAccordionThresholds.length - 1]!.maxHeadingCount;
  }, [windowDimensions.height]);

  function renderHymnalHeadingAndHymns({
    headingIndex,
    headingCount,
    firstChildIndex,
    hymnal,
    displayHymnalTitle,
    lastHymnalNameIsWrapped,
  }: {
    headingIndex: number;
    headingCount: number;
    firstChildIndex: number;
    hymnal: HymnalWithHymns;
    displayHymnalTitle: boolean;
    lastHymnalNameIsWrapped: boolean;
  }): (ReactElement | null)[] {
    const visibleHymns = getVisibleHymns(hymnal);
    const nthChild = firstChildIndex + 1;

    function scrollIntoView() {
      // Because the headings are sticky ("This Week", "Last Week"), if we scroll them into view, the scroll calculation
      // will be based on where the header is stuck rather than where it currently resides in the scrolled list,
      // causing it not to scroll into the correct location.
      // For example, say the header is stuck to the bottom, but its current position is actually off the bottom
      // of the screen.  When we scroll it into view, the scroll calculation will determine the distance from its
      // stuck location to the desired scroll location and scroll by that amount.  This will not scroll far enough.
      // Instead, it should have calculated based on the distance from where it currently resides in the scrolled
      // list, which is off the bottom of the screen.
      // So, we scroll based on the first child of the header, which is not sticky, and therefore always scrolls to the
      // correct location on the screen, causing the header to also be positioned correctly.
      // Both Safari & Chrome exhibit the same behavior - not sure about other browsers, but don't really care.
      const firstHymnId = (nthChild + 1).toString() + '-hymn';
      document.getElementById(firstHymnId)?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }

    function wrapHymnalName(hymnal: HymnalWithHymns): ReactElement {
      const fullHymnalName = hymnal.name;
      const hymnalName = (
        <HymnalNameWithEditButton key={fullHymnalName + 'hymnal-name-with-edit-button'}>
          <HymnalName key={fullHymnalName + 'hymnal-name'} $disabled={!songClickIsEnabled(hymnal)}>
            {hymnalNameShouldBeWrapped(fullHymnalName) ? fullHymnalName.split('-')[0] : fullHymnalName}
          </HymnalName>
          {hymnal.isPreview && <PreviewBadge>preview</PreviewBadge>}
          {hymnal.showRedDot && (
            <RedDotWrapper>
              &nbsp;&nbsp;
              <RedDotIcon />
            </RedDotWrapper>
          )}
          {onEditHymnal &&
            (isOrgAdmin || isInternalUser || isStaff) &&
            !showThisSundayOnly &&
            !searchString &&
            !isDemo && <EditHymnalButton key={'edit-' + fullHymnalName} onClick={onEditHymnal} hymnal={hymnal} />}
        </HymnalNameWithEditButton>
      );
      let hymnalNameParts: ReactElement[];
      if (!hymnalNameShouldBeWrapped(fullHymnalName)) {
        hymnalNameParts = [hymnalName];
      } else {
        hymnalNameParts = [
          hymnalName,
          <HymnalNameSecondLine key={fullHymnalName + '-hymnalNameSecondLine'}>
            {fullHymnalName.split('-')[1]?.trim()}
          </HymnalNameSecondLine>,
        ];
      }
      return (
        <HymnalNameRow key={fullHymnalName + '-hymnal-name-row'}>
          <HymnalNameGroup key={fullHymnalName + '-hymnal-name-group'}>{hymnalNameParts}</HymnalNameGroup>
        </HymnalNameRow>
      );
    }

    const songList = searchString ? getSongListFromHymns('Search', listOfHymns) : getSongListFromHymnal(hymnal);
    const isHymnalGated = songClickIsGated(hymnal);
    const isHymnalClickEnabled = songClickIsEnabled(hymnal);

    const tableRows = hymnal.message
      ? [
          <HymnalMessageWrapper key={hymnal.name + '-message'}>
            <HymnalMessage key={'hymnal-message'}>{hymnal.message}</HymnalMessage>
          </HymnalMessageWrapper>,
        ]
      : [...visibleHymns.slice(0, maxSearchResultsPerHymnal + 1).entries()].map(([index, hymn]) => {
          const hymnUrl = generateHymnUrl({
            // TODO(hewitt): Should be using React router to route to hymn, and should also be using song slug as URL
            hymnals: allHymnals,
            hymn,
            page: isDemo || showThisSundayOnly ? Pages.Library : page,
          });

          const thisRowNthChild = nthChild + index + 1;
          const hymnId = thisRowNthChild.toString() + '-hymn';

          function onMouseOver() {
            if (!isInSongSelector || ignoreMouseHover) {
              return;
            }
            const absoluteIndex = listOfHymns.indexOf(hymn);
            if (absoluteIndex === -1) {
              return;
            }
            setSelectedSongIndex(absoluteIndex);
          }

          if (index === maxSearchResultsPerHymnal) {
            return (
              <HymnRow
                key={hymnal.name + '-ellipsis'}
                $isDisabled={false}
                $isSelected={false}
                $roundedCorners={!!isInSongSelector}
              >
                <EllipsisCell key={'ellipsis'}>...</EllipsisCell>
              </HymnRow>
            );
          }

          function navigateToSong() {
            if (!hymnUrl) {
              return;
            }
            const newIndex = songList.songs
              .filter((entry) => isBound(entry))
              .findIndex(({ slug }) => slug === hymn.slug);
            const playlistIndex = newIndex >= 0 ? newIndex : 0;
            if (isHymnalGated) {
              void openSubscriptionSongGate({
                navigate,
                originPage: page,
                playlist: songList,
                playlistIndex,
                songTitle: hymn.title,
              });
              setSelectedSongIndex(playlistIndex);
              return;
            }
            if (!isHymnalClickEnabled) {
              return;
            }
            if (onSongClick) {
              return onSongClick(hymn.slug);
            }
            openPlayer({ playlist: songList, initialSlug: hymn.slug });
            setSelectedSongIndex(playlistIndex);
          }

          const numberCell = <SongNumber key={'number'}>{isCustomSong(hymn.slug) ? '' : hymn.songNumber}</SongNumber>;

          // TODO(hewitt): the full match is because cc2020 697 title "Abide with Me - Fast Falls the Eventide"
          //               contains a dash, causing the psalm to be set to 'Fast Falls the Eventide',
          //               and it is unclear where this happens.  The hymns list code needs to be rewritten,
          //               at which time this will become clear.
          const psalmNumber = hymn.psalm?.match(/^(?:Psalm|Psaume)\s+(?<number>\d+)$/)?.groups?.number;
          const psalmText = psalmNumber && `Ps. ${psalmNumber}`;

          const isSelected =
            getPlatform() !== Platform.Mobile &&
            searchIsVisible &&
            selectedSongIndex !== undefined &&
            listOfHymns[selectedSongIndex] === hymn;

          const charCount = psalmText ? psalmText.length - 1 : 0;
          const psalmCell = (
            <PsalmCell key={'psalm'}>
              <Psalm $charCount={charCount}>{psalmText}</Psalm>
            </PsalmCell>
          );

          const key = hymnal.name + ' ' + hymn.songNumber + ' ' + hymn.title + ' ' + thisRowNthChild.toString();
          const isDisabled = isSongRowDimmed({
            hymn,
            isGated: isHymnalGated,
            isSongIssueSuppressed: suppressSongIssue(hymnal, hymn),
            isClickEnabled: isHymnalClickEnabled,
          });
          const nameCell = (
            <SongDisplayName
              key="name"
              hymn={hymn}
              isDimmed={isDisabled}
              searchRegEx={searchRegEx}
              isInSongEditMode={isInSongEditMode}
            />
          );

          const playButtonCell =
            isSelected && playButton ? <React.Fragment key="play">{playButton}</React.Fragment> : undefined;

          const content = [numberCell, nameCell, hymn.psalm ? psalmCell : null, playButtonCell];

          // It is very expensive to use a custom-styled component for every row in the hymnal when only the
          // first hymn after the heading is used for scrolling.  Thus, we only use a non-custom styled component (below)
          // for all the rest of the hymns.
          if (index === 0) {
            return (
              <ScrollableHymnRow
                key={key}
                id={hymnId}
                onClick={navigateToSong}
                onMouseOver={onMouseOver}
                $headingIndex={headingIndex}
                $nthChild={thisRowNthChild}
                $isDisabled={isDisabled}
                $isSelected={isSelected}
                $roundedCorners={!!isInSongSelector}
              >
                {content}
              </ScrollableHymnRow>
            );
          }
          return (
            <HymnRow
              key={key}
              id={hymnId}
              onClick={navigateToSong}
              onMouseOver={onMouseOver}
              $isDisabled={isDisabled}
              $isSelected={isSelected}
              $roundedCorners={!!isInSongSelector}
            >
              {content}
            </HymnRow>
          );
        });

    let hymnalHeading = null;
    if (displayHymnalTitle && (hymnal.id !== PrimaryOrgHymnalId || searchString)) {
      hymnalHeading =
        headingCount > maxHeadingCount || currentOrg?.type === OrgType.School ? (
          <HymnalHeading key={hymnal.name + '-heading'} onClick={scrollIntoView}>
            {wrapHymnalName(hymnal)}
          </HymnalHeading>
        ) : (
          <HymnalHeadingWithAccordion
            key={hymnal.name + '-heading'}
            $headingIndex={headingIndex}
            $headingCount={headingCount}
            $nthChild={nthChild}
            $lastHymnalNameIsWrapped={lastHymnalNameIsWrapped}
            onClick={scrollIntoView}
          >
            {wrapHymnalName(hymnal)}
          </HymnalHeadingWithAccordion>
        );
    }

    return [hymnalHeading, ...tableRows];
  }

  const onCancelSearch = useCallback(async () => {
    if (isInSongSelector && searchString) {
      return;
    }
    setSearchIsVisible(false);
    if (shouldActivateSearch) {
      if (onClose) {
        onClose();
      } else {
        await navigate(getUrlForPage(Pages.Today));
      }
    }
  }, [isInSongSelector, searchString, shouldActivateSearch, onClose, navigate]);

  const onSearchKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !searchString) {
        onSongClick?.(undefined); // clear song
      }
    },
    [onSongClick, searchString],
  );

  const onDeleteButtonClick = useCallback(() => {
    onSongClick?.(undefined);
    onClose?.();
  }, [onClose, onSongClick]);

  const onSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key.length === 1 && loading) {
        setSearchString(searchString + event.key); // avoid dropping keystrokes while we are loading
        event.preventDefault(); // avoids duplicate keystrokes on Safari
      } else {
        onSearchKeyDownProp?.(event, searchString);
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
      }
    },
    [loading, onSearchKeyDownProp, searchString, setSearchString],
  );

  const scrollableDivRef = useRef<HTMLDivElement>(null);

  function resetScrollPosition() {
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTop = 0;
    }
  }

  const onAddCustomSong = useCallback(() => {
    onAddSong?.(searchString);
  }, [onAddSong, searchString]);

  function renderHymnals() {
    if (isFancyHymnalListVisible) {
      return (
        <HymnalsView
          hymnals={visibleHymnals}
          onHymnalClick={setCurrentHymnal}
          songClickIsEnabled={songClickIsEnabled}
          headerAfterFirstHymnal={headerAfterFirstHymnal}
        />
      );
    }

    let childCounter = 0;
    const lastHymnalNameIsWrapped =
      visibleHymnals.length > 0 && hymnalNameShouldBeWrapped(visibleHymnals[visibleHymnals.length - 1]!.name);

    const allButLastHymnal = visibleHymnals.slice(0, visibleHymnals.length - 1);
    const fancyFirstHymnal = allButLastHymnal.length > 0 &&
      areTopHymnalsFancy &&
      !searchString &&
      !showThisSundayOnly &&
      !currentHymnal && (
        <HymnalsView
          hymnals={allButLastHymnal}
          onHymnalClick={setCurrentHymnal}
          songClickIsEnabled={songClickIsEnabled}
          bottomBorder={true}
        />
      );

    return (
      <Fragment key="hymnals">
        {firstElement}
        {fancyFirstHymnal}
        <HymnsListWrapper key="this-week-table">
          {[
            ...visibleHymnals.filter((hymnal) => !fancyFirstHymnal || !allButLastHymnal.includes(hymnal)).entries(),
          ].map(([headingIndex, hymnal]) => {
            const result = renderHymnalHeadingAndHymns({
              headingIndex,
              // don't include primary hymnal in the count of headings b/c it is rendered as a fancy link
              // super hacky - the fancy hymnal link should be rendered by the Organization page
              headingCount: visibleHymnals.length - (visibleHymnals[0]?.id === PrimaryOrgHymnalId ? 1 : 0),
              firstChildIndex: childCounter,
              hymnal,
              displayHymnalTitle,
              lastHymnalNameIsWrapped,
            });
            const headerCount = displayHymnalTitle ? 1 : 0;
            const ellipsisCount = 1;
            const messageCount = 1;
            childCounter +=
              hymnal.hymns.length > 0
                ? Math.min(hymnal.hymns.length + headerCount, maxSearchResultsPerHymnal + ellipsisCount + headerCount)
                : messageCount + headerCount;
            return result;
          })}
        </HymnsListWrapper>
        {(isOrgAdmin || isInternalUser) && searchString && onAddSong && (
          <AddCustomSong onClick={onAddCustomSong}>Add Song</AddCustomSong>
        )}
        {!visibleHymnals?.length && !searchHasFocus && !searchString && !weeklySongGroups && <Spinner key="spinner" />}
      </Fragment>
    );
  }

  const onSearchIconClick = useCallback(() => {
    // onSearchIconClick is also invoked from the Cmd/Ctrl-F keydown listener (async, not flagged)
    // and from the shouldActivateSearch effect below (flagged). The effect-driven activation is
    // required here for deep-links that open the page with ?activateSearch.
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setSearchIsVisible(true);
  }, []);

  useEffect(() => {
    if (shouldActivateSearch) {
      onSearchIconClick();
    }
  }, [shouldActivateSearch, onSearchIconClick]);

  useEffect(() => {
    const controller = new AbortController();
    document.addEventListener(
      'keydown',
      (event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
          onSearchIconClick();
          event.preventDefault();
        }
      },
      { signal: controller.signal },
    );
    return () => controller.abort();
  }, [onSearchIconClick]);

  useEffect(() => {
    const controller = new AbortController();
    // this is pretty ugly
    // it is useful because it hides the mobile keyboard when the song list scrolls, but there must be a better way...
    const searchInputElement = document.getElementById('song-search-bar');
    const hymnsTableElement = document.getElementById('song-list');
    hymnsTableElement?.addEventListener('touchmove', () => searchInputElement?.blur(), { signal: controller.signal });
    return () => controller.abort();
  }, []);

  const isInFavoritesPage = hymnals.length === 1 && hymnals[0].id === FavoritesHymnalId;

  const isBackButtonVisible = (singleHymnal || onClose) && !isInFavoritesPage;
  const onHymnalBack = useCallback(() => {
    if (onClose && !singleHymnal) {
      onClose();
      return;
    }
    resetScrollPosition();
    setCurrentHymnal(undefined);
  }, [onClose, singleHymnal]);

  // hide footer in demo mode or if on-screen keyboard visible
  const hideFooter =
    (isDemo && !isFooterVisibleForDemo) || showThisSundayOnly || (searchHasFocus && isInsideMobileApp()) || onSongClick;

  return (
    <OuterPageContent key="outer" $noHeight={showThisSundayOnly} onKeyUp={onKeyUp}>
      {searchIsVisible || showThisSundayOnly ? undefined : customHeader ? (
        customHeader
      ) : (
        <>
          <Header
            key="header"
            title={hymnsListTitle}
            onBack={isBackButtonVisible ? onHymnalBack : undefined}
            isUploadSongButtonVisible={isUploadSongButtonVisible}
            suppressIcons={isDemo}
            onSearchClick={onSearchIconClick}
            onEditClick={onHeaderEditClick}
            onTutorialsClick={onTutorialsClick}
            showProfileAvatar={showProfileAvatar}
          />
        </>
      )}
      {!isDemo && !showThisSundayOnly && (searchIsVisible || activateSearch) && (
        <SearchControlWrapper $visible={searchIsVisible}>
          {deleteButtonVisible && <DeleteButton onClick={onDeleteButtonClick} />}
          {isInSongSelector && currentHymnal && <SearchBackArrow onClick={onHymnalBack} />}
          <SearchControl
            onKeyDown={onSearchKeyDown}
            onKeyUp={onSearchKeyUp}
            onCancel={onCancelSearch}
            selectContent={activateSearch && selectSearchValue}
            retainFocusOnCancel={isInSongSelector && !!searchString}
          />
        </SearchControlWrapper>
      )}
      {!showThisSundayOnly && <SubscriptionBanner key="subscription-banner" />}
      <InnerPageContent key="inner" id="song-list" ref={scrollableDivRef}>
        {(!message || isDemo || searchString || searchHasFocus) && (hymnals.length > 0 ? renderHymnals() : <Spinner />)}
        {message && !searchHasFocus && searchString === '' ? (
          <Message key="message" $topMargin={isFooterVisibleForDemo ? '10px' : '40px'}>
            {message}
          </Message>
        ) : null}
      </InnerPageContent>
      {hideFooter ? undefined : <Footer key="footer" />}
    </OuterPageContent>
  );
};

async function openSubscriptionSongGate({
  navigate,
  originPage,
  playlist,
  playlistIndex,
  songTitle,
}: {
  navigate: NavigateFunction;
  originPage?: Pages;
  playlist: SongList;
  playlistIndex: number;
  songTitle?: string;
}) {
  storeProspectiveSubscriptionOrigin({ originPage, playlist, playlistIndex });
  await navigate(getUrlForPage(Pages.SubscriptionTrial), { state: { songTitle } });
}

const HymnalHeading = styled.div`
  z-index: 1;
  color: var(--color-section-heading);
  font-family: Jost-SemiBold, 'Arial', sans-serif;
  font-size: 1.5em;
  text-align: left;
  position: sticky;
  top: 0;
  background-color: var(--color-background);
  cursor: pointer;
  padding-left: 10px;
`;

const HymnalHeadingWithAccordion = styled(HymnalHeading)<{
  $headingIndex: number;
  $headingCount: number;
  $nthChild: number;
  $lastHymnalNameIsWrapped: boolean;
}>`
  &:nth-child(${(props) => props.$nthChild}) {
    top: ${(props) => props.$headingIndex * 1.75}em;
    bottom: ${(props) =>
      (props.$headingCount - props.$headingIndex - 1) * 1.75 +
      (props.$lastHymnalNameIsWrapped && props.$headingIndex !== props.$headingCount - 1 ? 1.5 : 0)}em;
  }
`;

const HymnalNameRow = styled.div`
  padding-top: 0.5em;
`;

const HymnalNameGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// for weekly song lists
const HymnalName = styled.div<{ $disabled: boolean }>`
  color: ${(props) => (props.$disabled ? 'var(--color-text-disabled)' : 'inherit')};
  text-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RedDotWrapper = styled.div`
  position: relative;
`;

const HymnalNameSecondLine = styled.div`
  font-family: Jost-Regular, 'Arial', sans-serif;
  font-size: 0.7em;
  padding-bottom: 0.5em;
`;

const HymnalMessageWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const HymnalMessage = styled.div`
  white-space: normal;
  text-align: center;
  max-width: 28ch;
`;

const HymnRow = styled.div<{
  $isDisabled: boolean;
  $isSelected: boolean;
  $roundedCorners: boolean;
}>`
  display: flex;
  position: relative;
  ${
    WrapHymnNames
      ? `
    align-items: top;
  `
      : `
    align-items: center;
  `
  }
  height: ${HymnRowHeight};
  white-space: normal;
  cursor: pointer;
  ${(props) => (props.$roundedCorners ? 'border-radius: 10px;' : '')}
  ${(props) =>
    props.$isDisabled
      ? `
      color: var(--color-text-disabled);
      fill: var(--color-text-disabled); 
    `
      : `
      color: var(--color-text);
      fill: var(--color-text); 
    `};
  ${(props) =>
    props.$isSelected
      ? `
      background: var(--color-background-selected-item);
    `
      : `
      background: var(--color-background); 
    `};
`;

const EllipsisCell = styled.div`
  font-family: Jost-SemiBold, 'Arial', sans-serif;
  letter-spacing: 3px;
`;

const ScrollableHymnRow = styled(HymnRow)<{
  $headingIndex: number;
  $nthChild: number;
}>`
  &:nth-child(${(props) => props.$nthChild}) {
    // TODO(hewitt): This calculation is not quite right, but close enough for now.
    //               The idea is that the first hymn following a header should scroll up to just below the header.
    scroll-margin-top: ${(props) => (props.$headingIndex + 2) * 2.6 - 1.6}em;
  }
`;

const SongNumber = styled.div`
  width: 4ch;
  text-align: right;
  text-wrap: nowrap;
  // font-family: Jost-SemiBold, Arial, sans-serif;
  // font-size: 1.5rem;
`;

const PsalmCell = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--color-psalm-ref);
`;

const Psalm = styled.div<{ $charCount: number }>`
  text-align: right;
  width: ${(props) => props.$charCount}ch;
  ${(props) =>
    props.$charCount === 0
      ? ''
      : `
    padding-right: 5px; 
  `};
  text-wrap: nowrap;
`;

function EditHymnalButton({
  hymnal,
  onClick,
}: {
  hymnal: HymnalWithHymns;
  onClick: (hymnal: HymnalWithHymns) => void;
}) {
  return <Pencil onClick={() => onClick(hymnal)} />;
}

const HymnalNameWithEditButton = styled.div`
  display: flex;
  align-items: center;
`;

const PreviewBadge = styled.div`
  font-family: Jost-Regular, sans-serif;
  font-size: 0.55em;
  color: var(--color-text-singing-plan-callout);
  background-color: lch(26.5% 97 307 / 0.12);
  border-radius: 10px;
  padding: 1px 10px;
  margin-left: 12px;
`;

const DeleteButton = styled(TrashIcon)`
  margin-right: 5px;
  fill: var(--color-trash);
  background-color: var(--color-background);
  padding: 4px;
  border-radius: 10px;
  width: 40px;
  height: 35px;
  cursor: pointer;
`;

const HymnsListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  border-spacing: 0;
  position: relative;
`;

const SearchBackArrow = styled(BackArrow)`
  padding-right: 10px;
`;

const AddCustomSong = styled.button`
  margin-top: 10px;
  padding: 5px 10px;
  border: 3px solid var(--color-text);
  border-radius: 15px;
  cursor: pointer;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.3em;
  width: fit-content;
  background: transparent;
  &:hover {
    background-color: lightgray;
  }
`;

const Message = styled.div<{ $topMargin: string }>`
  white-space: normal;
  margin: ${(props) => props.$topMargin} 30px;
  padding: 3px 5px;
  color: var(--color-text-singing-plan-callout);
`;

const SearchControlWrapper = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  text-align: center;
  width: ${(props) => (!props.$visible ? '0' : '100%')};
`;
